/**
 * Plans bought in the Solmi iOS / Android app are billed by the App Store or
 * Google Play, not Stripe, so neither the Stripe billing portal nor the web
 * cancel flow can touch them. On Oct 9 2026 a Google Play subscriber who
 * opened /subscription on a desktop to cancel got "No active subscription
 * found" from every button, with nothing saying where the plan was billed.
 *
 * payment.getSubscription reports `billingStore` so the web page can send
 * these subscribers to the store instead, and `storePlanActive` so the web
 * plan pickers don't start a second, Stripe-billed plan on top.
 */
export type BillingStore = "google_play" | "app_store";

/** The store's own manage-subscriptions page (cancel, change plan, payment). */
export const STORE_SUBSCRIPTIONS_URL: Record<BillingStore, string> = {
  google_play: "https://play.google.com/store/account/subscriptions",
  app_store: "https://apps.apple.com/account/subscriptions",
};

/**
 * The store that bills a subscriptions row, or null when Stripe (or nobody)
 * does. A Stripe subscription id wins: iapPlatform is never cleared, so a
 * user who moved from the app to a web plan still carries the old store.
 */
export function billingStoreForSubscription(
  row:
    | { stripeSubscriptionId?: string | null; iapPlatform?: string | null }
    | null
    | undefined,
): BillingStore | null {
  if (!row || row.stripeSubscriptionId) return null;
  if (row.iapPlatform === "android") return "google_play";
  if (row.iapPlatform === "ios") return "app_store";
  return null;
}

/**
 * How long after its period ends a store subscription can still renew: Apple
 * retries a failed renewal for up to 60 days, and Google's grace period plus
 * account hold add up to at most 60 days as well.
 */
export const STORE_BILLING_RETRY_MS = 60 * 24 * 60 * 60 * 1000;

/**
 * Whether the store that bills this row (billingStoreForSubscription) still
 * runs its paid plan: inside the paid period, or retrying a failed renewal
 * (past_due). A web plan bought on top would be billed twice, and the store's
 * own sync keeps writing its plan back over the web one, so
 * payment.createSubscriptionCheckout refuses it (Oct 9 2026).
 *
 * False once the period has ended. Google's renewal notifications never reach
 * us and nothing downgrades an expired Play plan, so on Oct 9 2026 there were
 * 424 Android rows still reading as paid and active weeks after Google stopped
 * billing them; those users can still subscribe on the web. A row whose
 * period end was never recorded counts as running.
 */
export function storePlanActive(
  row:
    | {
        stripeSubscriptionId?: string | null;
        iapPlatform?: string | null;
        plan?: string | null;
        status?: string | null;
        iapExpiresAt?: Date | null;
        currentPeriodEnd?: Date | null;
      }
    | null
    | undefined,
  now: Date = new Date(),
): boolean {
  if (!row || !billingStoreForSubscription(row)) return false;
  if (!row.plan || row.plan === "free" || row.status === "canceled") {
    return false;
  }
  const periodEnd = row.iapExpiresAt ?? row.currentPeriodEnd;
  if (!periodEnd) return true;
  const end = new Date(periodEnd).getTime();
  if (end > now.getTime()) return true;
  return (
    row.status === "past_due" && end > now.getTime() - STORE_BILLING_RETRY_MS
  );
}
